import { Star, Trash2 } from 'lucide-react';
import { CardStatus, type BankCardSummary } from '@shared/types';

import { CardStatusBadge } from '@/components/ui/Badge';
import { cn } from '@/lib/cn';
import { absoluteDate } from '@/lib/format';

/**
 * Card-shaped, at roughly the real aspect ratio, so a trader with four cards
 * recognises the right one at a glance instead of reading a table row. Status
 * lives on the plate itself, since an unverified card takes no traffic.
 *
 * The root is a plain element rather than a list item: the card list pairs each
 * plate with its limits and usage block inside one shared list item, and a
 * plate that insisted on being the row could not be composed that way.
 */
export function BankCardItem({
  card,
  onRemove,
  isRemoving,
}: {
  card: BankCardSummary;
  onRemove?: (cardId: string) => void;
  isRemoving?: boolean;
}) {
  const isRejected = card.status === CardStatus.Rejected;

  return (
    <div
      className={cn(
        'relative flex aspect-[1.6/1] max-w-[20rem] flex-col justify-between',
        'rounded-lg border p-4',
        isRejected ? 'border-danger-500 bg-danger-50' : 'border-line-strong bg-sunken',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-base font-semibold text-fg">{card.bank}</p>
          {card.isPrimary ? (
            <p className="mt-0.5 inline-flex items-center gap-1 text-2xs font-semibold uppercase tracking-wide text-fg-muted">
              <Star size={10} strokeWidth={1.5} aria-hidden="true" />
              Основная
            </p>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <CardStatusBadge status={card.status} />
          {onRemove ? (
            <button
              type="button"
              onClick={() => onRemove(card.id)}
              disabled={isRemoving}
              aria-label={`Удалить карту ${card.bank} с окончанием ${card.last4}`}
              className={cn(
                // A real 44x44 box, not an expanded pseudo-element: the status
                // badge sits within 44px of it, and an overlay would put
                // "delete card" under a tap aimed at the badge. The negative
                // margin absorbs the extra size so the plate keeps its layout.
                'focus-ring -m-2 flex h-11 w-11 items-center justify-center rounded-sm',
                'text-fg-muted transition-colors duration-fast',
                'hover:bg-surface hover:text-danger-500',
                'disabled:cursor-not-allowed disabled:opacity-40',
              )}
            >
              <Trash2 size={16} strokeWidth={1.5} aria-hidden="true" />
            </button>
          ) : null}
        </div>
      </div>

      <div>
        <p className="num text-lg font-medium tracking-tight text-fg">
          <span aria-hidden="true">•••• •••• •••• </span>
          <span className="sr-only">Карта с окончанием </span>
          {card.last4}
        </p>
        {card.paymentDetailsReady === false && <p className="mt-2 text-xs text-danger-600">Для приёма платежей заново добавьте эту карту с полным номером и пройдите проверку. В старой записи сохранены только последние цифры.</p>}
        <div className="mt-2 flex items-end justify-between gap-3">
          <div className="min-w-0">
            <p className="eyebrow truncate text-fg-secondary">Держатель карты</p>
            <p className="mt-0.5 truncate text-sm uppercase text-fg-secondary">
              {card.holderName}
            </p>
          </div>
          <p className="num shrink-0 text-2xs text-fg-secondary">{absoluteDate(card.createdAt)}</p>
        </div>
      </div>

    </div>
  );
}
